import { ArrowLeft } from 'lucide-react';
import { apps, tools, type AppConfig } from './config';

function AppCard({ app, index }: { app: AppConfig; index: number }) {
  const Icon = app.icon;
  return (
    <a
      href={app.url}
      className="app-card"
      style={
        {
          '--accent': app.accent,
          '--accent-glow': app.accentGlow,
          '--delay': `${index * 120}ms`,
        } as React.CSSProperties
      }
    >
      {/* Step badge (funnel apps only) */}
      {app.step && <div className="step-badge">{app.step}</div>}

      {/* Icon */}
      <div className="card-icon">
        <Icon size={32} strokeWidth={1.5} />
      </div>

      {/* Text */}
      <h2 className="card-name text-balance">{app.name}</h2>
      <p className="card-subtitle">{app.subtitle}</p>
      <p className="card-desc">{app.description}</p>

      {/* CTA */}
      <div className="card-cta">
        <span>כניסה למערכת</span>
        <ArrowLeft size={16} />
      </div>

      {/* Hover glow */}
      <div className="card-glow" />
    </a>
  );
}

function App() {
  return (
    <div className="portal-root">
      {/* Animated background */}
      <div className="bg-layer">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="noise-overlay" />
      </div>

      {/* Standalone tools (not part of the funnel): buttons in the top-left corner */}
      <nav className="tool-links">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <a
              key={tool.id}
              href={tool.url}
              className="tool-link"
              aria-label={tool.name}
              title={tool.name}
              style={{ '--accent': tool.accent } as React.CSSProperties}
            >
              <Icon size={22} strokeWidth={1.75} />
              <span>{tool.name}</span>
            </a>
          );
        })}
      </nav>

      {/* Content */}
      <div className="content">
        {/* Hero */}
        <header className="hero">
          <div className="hero-logo-wrap">
            <img src="/logo/elka-logo.png" alt="Elka Logo" className="hero-logo" />
          </div>
          <h1 className="hero-title text-balance">פורטל אלקה</h1>
          <h2 className="hero-subtitle text-balance">מערכות ניהול מתקדמות</h2>
          <p className="hero-tagline">מקצה לקצה — מלקוח ועד פרויקט</p>
        </header>

        {/* Flow pipeline */}
        <div className="pipeline">
          {/* Connecting line (desktop) */}
          <svg className="flow-line" viewBox="0 0 1200 8" preserveAspectRatio="none">
            <defs>
              {/* Under dir="rtl" the cards lay out physically right-to-left, so card 01 (purple)
                  is on the physical right and card 04 (emerald) is on the physical left.
                  Stops are ordered to match: emerald at x=0% (left), purple at x=100% (right). */}
              <linearGradient id="flow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="33%" stopColor="#06b6d4" />
                <stop offset="66%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
            <line
              x1="0"
              y1="4"
              x2="1200"
              y2="4"
              stroke="url(#flow-gradient)"
              strokeWidth="2"
              strokeDasharray="8 4"
              className="flow-line-path"
            />
          </svg>

          {/* Connecting line (mobile - vertical) */}
          <svg className="flow-line-vertical" viewBox="0 0 8 800" preserveAspectRatio="none">
            <defs>
              <linearGradient id="flow-gradient-v" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="33%" stopColor="#f59e0b" />
                <stop offset="66%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
            <line
              x1="4"
              y1="0"
              x2="4"
              y2="800"
              stroke="url(#flow-gradient-v)"
              strokeWidth="2"
              strokeDasharray="8 4"
              className="flow-line-path-v"
            />
          </svg>

          {/* App cards */}
          {apps.map((app, index) => (
            <AppCard key={app.id} app={app} index={index} />
          ))}
        </div>

        {/* Pipeline flow label */}
        <div className="pipeline-label">
          <span className="pipeline-label-text">לקוח</span>
          <span className="pipeline-label-arrow-char" />
          <span className="pipeline-label-text">הצעת מחיר</span>
          <span className="pipeline-label-arrow-char" />
          <span className="pipeline-label-text">ניהול פרויקט</span>
          <span className="pipeline-label-arrow-char" />
          <span className="pipeline-label-text">ביצוע</span>
        </div>

        {/* Footer */}
        <footer className="portal-footer">
          <p>אלקה חשמל בע״מ &copy; {new Date().getFullYear()}</p>
        </footer>
      </div>
    </div>
  );
}

export default App;
